import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Container,
  Card,
  CardContent,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import homebg from '../../Assets/homebg.png';
import BottomNavigation from '../BottomNavigation/BottomNavigation';
import config from '../../Config';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const BOOKING_SESSION_UPDATED_EVENT = "bookingSessionUpdated";

const notifyBookingSessionUpdated = () => {
  window.dispatchEvent(new Event(BOOKING_SESSION_UPDATED_EVENT));
};

const BOOKING_SESSION_KEYS = [
  "currentBookingIndex",
  "bookingDocumentID",
  "propertyDocumentID",
  "reservationID",
  "hasUpSale",
  "bookingRooms",
  "userBookings",
];

export const clearBookingSession = () => {
  BOOKING_SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k));
  notifyBookingSessionUpdated();
};

export default function HomeScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [topPicks, setTopPicks] = useState([]);
  const [topPicksLoading, setTopPicksLoading] = useState(false);
  const [propertyCity, setPropertyCity] = useState('');
  const [bookings, setBookings] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentHasUpSale, setCurrentHasUpSale] = useState(true);
  const scrollRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const [locationAnchorEl, setLocationAnchorEl] = useState(null);
  const locationMenuOpen = Boolean(locationAnchorEl);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(accountAnchorEl);

const updateActiveBooking = async (booking, index, token) => {
  setActiveIndex(index);

  sessionStorage.setItem('currentBookingIndex', index.toString());
  sessionStorage.setItem('bookingDocumentID', booking.bookingDocumentID);
  sessionStorage.setItem('propertyDocumentID', booking.propertyDocumentID);
  sessionStorage.setItem('reservationID', booking.reservationID);
  sessionStorage.setItem('hasUpSale', booking.hasUpSale ? 'true' : 'false');
  sessionStorage.setItem('bookingRooms', JSON.stringify(booking.rooms || [])); 

  notifyBookingSessionUpdated();
    setPropertyCity(booking.propertyCity);

    setTopPicks([]);
    setTopPicksLoading(true);

    if (booking.hasUpSale === false) {
      setTopPicks([]);
      setTopPicksLoading(false);
      setCurrentHasUpSale(false);
      return;
    }

    setCurrentHasUpSale(true);

    if (booking.propertyDocumentID && token) {
      try {
        const res = await fetch(
          `${config.apiBaseUrl}/guest/upsale/property/${booking.propertyDocumentID}/categories`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) throw new Error('Failed to fetch categories');

        const data = await res.json();

        const categoriesWithSubFlag = (data?.categories || []).map((cat) => ({
          _id: cat._id,
          name: cat.name,
          image: cat.image,
          isActive: cat.isActive,
          hasSubCategory: cat.hasSubCategory || false,
        }));

        setTopPicks(categoriesWithSubFlag);
      } catch (err) {
        setTopPicks([]);
      } finally {
        setTopPicksLoading(false);
      }
    } else {
      setTopPicksLoading(false);
    }
  };

  useEffect(() => {
    const savedIndex = sessionStorage.getItem('currentBookingIndex');
    if (savedIndex) setActiveIndex(Number(savedIndex));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const userEmail = localStorage.getItem('userEmail');
      const token = localStorage.getItem('token');

      if (!userEmail || !token) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${config.apiBaseUrl}/guest/user-Hotel-reservations/${userEmail}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error('Failed to fetch bookings');

        const data = await res.json();
        const fetchedBookings = data.data || [];

        if (fetchedBookings.length === 0) {
          setBookings([]);
          setLoading(false);
          return;
        }

        const bookingList = fetchedBookings.map((b) => ({
          bookingDocumentID: b._id,
          reservationID: b.reservationID,
          propertyDocumentID: b.property?._id || '',
          propertyID: b.propertyID || '',
          guestName: b.guestName || '',
          roomNumber: b.assigned?.[0]?.roomName || 'No room assigned',
          rooms: b.assigned || [],
          propertyName: b.property?.propertyName || '',
          propertyCity: b.property?.propertyAddress?.propertyCity || '',
          propertyState: b.property?.propertyAddress?.propertyState || '',
          image:
            b.property?.propertyImage?.[0]?.image || b.property?.propertyImage?.[0]?.thumb || null,
          checkInDate: b.startDate,
          reservationStatus: b.status,
          hasUpSale: b.property?.hasUpSale,
        }));

        sessionStorage.setItem('userBookings', JSON.stringify(bookingList));
        setBookings(bookingList);

        const currentIndex = Number(sessionStorage.getItem('currentBookingIndex')) || 0;
        const currentBooking = bookingList[currentIndex] || bookingList[0];

        await updateActiveBooking(currentBooking, currentIndex, token);
      } catch (err) {
        // console.error("Error fetching booking:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const scrollToIndex = (index, behavior = "smooth") => {
    if (!scrollRef.current || !bookings[index]) return;
    const container = scrollRef.current;
    const cardWidth = container.clientWidth;

    isProgrammaticScroll.current = true;
    container.scrollTo({
      left: index * cardWidth,
      behavior,
    });

    const token = localStorage.getItem('token');
    updateActiveBooking(bookings[index], index, token);
  };

  const handleDotClick = (index) => scrollToIndex(index);

  const handleScroll = () => {
    if (isProgrammaticScroll.current) return;

    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const cardWidth = container.clientWidth;
    const newIndex = Math.round(container.scrollLeft / cardWidth);

    if (newIndex !== activeIndex && bookings[newIndex]) {
      const token = localStorage.getItem('token');
      updateActiveBooking(bookings[newIndex], newIndex, token);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const tryRestore = () => {
      if (cancelled) return;
      if (bookings.length > 0 && scrollRef.current && scrollRef.current.firstChild) {
        const savedIndex = Number(sessionStorage.getItem('currentBookingIndex')) || 0;
        const container = scrollRef.current;
        const cardWidth = container.firstChild.offsetWidth + 16;
        container.scrollTo({
          left: savedIndex * cardWidth,
          behavior: 'smooth',
        });
        setActiveIndex(savedIndex);
      }
    };

    requestAnimationFrame(tryRestore);

    return () => {
      cancelled = true;
    };
  }, [bookings, loading]);

  const handleLocationClick = (event) => {
    if (bookings.length > 1) {
      setLocationAnchorEl(event.currentTarget);
    }
  };

  const handleLocationMenuClose = () => {
    setLocationAnchorEl(null);
  };

  const handleLocationSelect = (index) => {
    handleLocationMenuClose();
    if (index !== activeIndex) {
      handleDotClick(index);
    }
  };

  const handleAccountClick = (event) => {
    setAccountAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountAnchorEl(null);
  };

  const handleLogout = () => {
    handleAccountMenuClose();
    localStorage.clear();
    clearBookingSession();
    navigate("/");
  };

  const handleProceed = (booking) => {
    navigate(`/checkin/${booking.reservationID}`, {
      state: {
        reservationID: booking.reservationID,
        propertyDocumentID: booking.propertyDocumentID,
        propertyID: booking.propertyID,
        propertyName: booking.propertyName,
        rooms: booking.rooms,
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '400px',
        margin: '0 auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          mt: 1,
        }}
      >
        <IconButton onClick={handleAccountClick} size="small" sx={{ p: 0 }}>
          <Avatar src="/profile-avatar.png" sx={{ width: 48, height: 48 }} />
        </IconButton>

        <Menu
          anchorEl={accountAnchorEl}
          open={accountMenuOpen}
          onClose={handleAccountMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        <Box
          onClick={handleLocationClick}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            cursor: bookings.length > 1 ? 'pointer' : 'default',
          }}
        >
          <Typography variant="caption" sx={{ color: '#999', fontSize: '12px', mb: 0.5 }}>
            Location
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ color: '#e53e3e', fontSize: 16, mr: 0.5 }} />
            {loading ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : (
              <Typography
                variant="body1"
                sx={{
                  color: '#000',
                  fontWeight: 600,
                  fontSize: '15px',
                  mr: 0.5,
                }}
              >
                {propertyCity}
              </Typography>
            )}
            {!loading && bookings.length > 1 && (
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  color: "#000",
                  transition: "transform 0.2s",
                  transform: locationMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
          </Box>

          <Menu
            anchorEl={locationAnchorEl}
            open={locationMenuOpen}
            onClose={handleLocationMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            {bookings.map((b, idx) => (
              <MenuItem
                key={b.bookingDocumentID}
                selected={idx === activeIndex}
                onClick={() => handleLocationSelect(idx)}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2" fontWeight={600}>
                    {b.propertyCity || "Unknown city"}
                  </Typography>
                  {b.propertyName && (
                    <Typography variant="caption" color="text.secondary">
                      {b.propertyName}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <IconButton
          aria-label="Support"
          component="a"
          href="tel:02045259955"
          sx={{
            color: '#000',
            p: 0,
            cursor: 'pointer',
            backgroundColor: '#F8F8F8',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <HeadsetMicIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, px: 3, pb: 12 }}>
        {loading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={250}
            sx={{ borderRadius: '20px', mb: 4 }}
          />
        ) : (
          <>
            <Box
              ref={scrollRef}
              onScroll={handleScroll}
              sx={{
                display: 'flex',
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {bookings.map((booking) => (
                <Card
                  key={booking.bookingDocumentID}
                  sx={{
                    flex: '0 0 100%',
                    scrollSnapAlign: 'start',
                    borderRadius: '20px',
                    mb: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundImage: `url(${booking.image || homebg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    minHeight: { xs: '250px', sm: '300px' },
                    px: { xs: 2, sm: 3 },
                    py: { xs: 2, sm: 3 },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0,0,0,0.4)',
                      zIndex: 1,
                    }}
                  />
                  <CardContent
                    sx={{
                      p: 0,
                      color: 'white',
                      position: 'relative',
                      zIndex: 2,
                      minHeight: { xs: '250px', sm: '300px' },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    {/* TOP BLOCK */}
                    <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '24px', sm: '32px' },
                          mb: { xs: 2, sm: 4 },
                          textShadow: '0 2px 4px rgba(0,0,0,0.4)',
                        }}
                      >
                        {booking.guestName ? `Welcome ${booking.guestName}!` : 'Welcome!'}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '14px', sm: '16px' },
                          mb: 3,
                          opacity: 0.95,
                          lineHeight: 1.4,
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}
                      >
                        {(booking.rooms?.length ?? 0) > 1
                          ? `Your rooms are ${booking.roomNumber}. Enjoy your stay.`
                          : `Your room number is ${booking.roomNumber}. Enjoy your stay.`}
                      </Typography>
                    </Box>

                    {/* CENTER BUTTON */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => handleProceed(booking)}
                        sx={{
                          backgroundColor: '#2e7d7b',
                          color: 'white',
                          borderRadius: '12px',
                          px: { xs: 3, sm: 4 },
                          py: 1.5,
                          fontSize: { xs: '12px', sm: '14px' },
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                          '&:hover': { backgroundColor: '#1e5a58' },
                        }}
                      >
                        {booking.reservationStatus === 'confirmed'
                          ? 'Proceed for Online Check-In'
                          : 'Proceed to View Detail'}
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: { xs: 2, sm: 3 },
                        pb: { xs: 2, sm: 3 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '14px', sm: '16px' },
                          opacity: 0.95,
                          lineHeight: 1.4,
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}
                      >
                        {booking.propertyName}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '14px', sm: '16px' },
                          opacity: 0.95,
                          lineHeight: 1.4,
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}
                      >
                        {formatDate(booking.checkInDate)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 1 }}>
              {bookings.map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => handleDotClick(idx)}
                  sx={{
                    width: activeIndex === idx ? 12 : 8,
                    height: activeIndex === idx ? 12 : 8,
                    borderRadius: '50%',
                    backgroundColor: activeIndex === idx ? '#2e7d7b' : '#ccc',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                  }}
                />
              ))}
            </Box>

            {currentHasUpSale && (
              <>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: '22px', color: '#000', mb: 3 }}
                >
                  Top picks for you
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                    mb: 4,
                  }}
                >
                  {topPicksLoading ? (
                    Array.from({ length: 4 }).map((_, idx) => (
                      <Skeleton
                        key={idx}
                        variant="rectangular"
                        sx={{
                          borderRadius: '12px',
                          height: '120px',
                        }}
                      />
                    ))
                  ) : topPicks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No categories available
                    </Typography>
                  ) : (
                    topPicks.map((category) => (
                      <Card
                        key={category._id}
                        onClick={() =>
                          navigate(`/category/${category._id}`, {
                            state: { hasSubCategory: category.hasSubCategory },
                          })
                        }
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          height: '120px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Box
                          component="img"
                          src={category.image}
                          alt={category.name}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.2)',
                          }}
                        />
                        <CardContent sx={{ position: 'relative', zIndex: 2, p: 1.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, fontSize: '14px', color: '#fff' }}
                          >
                            {category.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Container>

      <BottomNavigation />
    </Box>
  );
}